import type {
  AIModel,
  ModelReasoningMode,
  ReasoningModeId,
} from '@affine/core/modules/ai-button/services/models';

const EFFECTIVE_REASONING_MODE_ORDER = [
  'low',
  'medium',
  'high',
  'xhigh',
] as const satisfies readonly ReasoningModeId[];

const EFFECTIVE_REASONING_MODE_LABELS: Record<
  (typeof EFFECTIVE_REASONING_MODE_ORDER)[number],
  string
> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'XHigh',
};

/** The stop for a model whose profile names no default among its slider stops. */
const DEFAULT_ACTIVE_REASONING_MODE = 'medium' satisfies ReasoningModeId;

function isEffectiveReasoningModeId(
  modeId: ReasoningModeId | undefined
): modeId is (typeof EFFECTIVE_REASONING_MODE_ORDER)[number] {
  return EFFECTIVE_REASONING_MODE_ORDER.includes(
    modeId as (typeof EFFECTIVE_REASONING_MODE_ORDER)[number]
  );
}

export function reasoningModeDisplayLabel(modeId: ReasoningModeId | undefined) {
  return isEffectiveReasoningModeId(modeId)
    ? EFFECTIVE_REASONING_MODE_LABELS[modeId]
    : '';
}

function effectiveReasoningModeFromProfileMode(
  mode: Pick<ModelReasoningMode, 'id' | 'enabled'>
): ReasoningModeId | null {
  if (!mode.enabled) return null;
  return isEffectiveReasoningModeId(mode.id) ? mode.id : null;
}

export function effectiveReasoningModesForModel(
  model: AIModel | undefined
): Array<{ id: ReasoningModeId; label: string }> {
  if (!model?.supportsReasoning) return [];

  const profileModes = model.reasoningProfile?.modes
    .map(effectiveReasoningModeFromProfileMode)
    .filter((modeId): modeId is ReasoningModeId => modeId !== null);

  const effectiveModes =
    profileModes && profileModes.length > 0
      ? profileModes
      : ([
          'low',
          'medium',
          'high',
        ] as const satisfies readonly ReasoningModeId[]);

  const modeIds = new Set(effectiveModes);
  return EFFECTIVE_REASONING_MODE_ORDER.filter(modeId =>
    modeIds.has(modeId)
  ).map(modeId => ({
    id: modeId,
    label: reasoningModeDisplayLabel(modeId),
  }));
}

function resolveEffectiveReasoningMode(
  model: AIModel,
  requestedMode: ReasoningModeId | undefined
): ReasoningModeId | undefined {
  const modes = effectiveReasoningModesForModel(model);
  if (modes.length === 0) return undefined;

  const modeIds = new Set(modes.map(mode => mode.id));
  if (requestedMode && requestedMode !== 'off' && modeIds.has(requestedMode)) {
    return requestedMode;
  }

  // The model's own default comes first: the registry sets it per model
  // (StreamLain AI Eco reasons at high).
  const defaultMode = model.reasoningProfile?.defaultMode;
  if (defaultMode && modeIds.has(defaultMode)) return defaultMode;

  if (modeIds.has(DEFAULT_ACTIVE_REASONING_MODE)) {
    return DEFAULT_ACTIVE_REASONING_MODE;
  }

  return (
    (['high', 'low', 'xhigh'] as const).find(modeId => modeIds.has(modeId)) ??
    modes[0]?.id
  );
}

/**
 * Whether the model's reasoning profile offers switching reasoning off. A
 * mode's `enabled` says whether it reasons, so the off mode is never enabled.
 */
export function reasoningProfileKnowsOff(model: AIModel): boolean {
  return model.reasoningProfile?.modes.some(mode => mode.id === 'off') ?? false;
}

export function resolveReasoningModel(
  models: readonly AIModel[],
  modelId: string | undefined
): AIModel | undefined {
  return modelId === 'auto'
    ? models.find(model => model.isDefault)
    : models.find(model => model.id === modelId);
}

export function resolveReasoningMode(
  model: AIModel | undefined,
  enabled: boolean | undefined,
  storedMode: ReasoningModeId | undefined
): ReasoningModeId | undefined {
  if (!model?.supportsReasoning) return undefined;

  // No stored choice means the model's default stop (medium where it names none). A
  // stored off counts only where the slider offers it; elsewhere the send
  // carries the stop the slider shows.
  if (enabled === false && reasoningProfileKnowsOff(model)) return 'off';

  return resolveEffectiveReasoningMode(model, storedMode);
}

export function isReasoningModeActive(
  model: AIModel | undefined,
  mode: ReasoningModeId | undefined
): boolean {
  return !!model?.supportsReasoning && !!mode && mode !== 'off';
}
